<template>
  <div
    v-if="active"
    v-scroll-lock="active"
    class="modal active"
  >
    <div class="modal-overlay"></div>

    <form
      class="modal-container modal-form modal-form--md"
      :disabled="loading"
      @submit.prevent="save"
    >
      <header class="modal-header modal-form__header">
        <a
          class="btn btn-clear float-right modal-form__close"
          aria-label="close"
          @click="close()"
        >
        </a>

        <h1 class="modal-form__title">
          Cadastrar paciente
        </h1>
      </header>

      <article class="modal-body modal-form__body">
        <div class="modal-form__wrapper">
          <div class="modal-form__item">
            <div class="modal-form__col modal-form__col--icon">
              <dr-icon
                :key="'category-icon'"
                ref="category-icon"
                name="inpersonOffice"
                fill="gray"
                class="modal-form__icon"
              />
            </div>

            <div class="modal-form__col">
              <div class="modal-form__field has-value">
                <div class="modal-form__select has-value">
                  <div>
                    <select
                      v-model="patientOfficeId"
                      :disabled="patientId"
                      id="doctorOffice"
                      class="form-select"
                    >
                      <option
                        v-for="office in doctorOffices.filter(o => o.type !== 'TELEMEDICINE')"
                        :key="office.id"
                        :value="office.id"
                        :selected="patientOfficeId === office.id"
                      >
                        {{ office.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-for="field in formData"
            :key="`specialPatientFormField_${field.name}`"
            :class="[field.hide && 'modal-form__item--hidden', field.class]"
            class="modal-form__item"
          >
            <div
              v-if="field.hasIcon"
              class="modal-form__col modal-form__col--icon"
            >
              <dr-icon
                v-if="field.icon"
                :name="field.icon"
                fill="gray"
                class="modal-form__icon"
              />
            </div>

            <div class="modal-form__col">
              <div class="modal-form__field">
                <dr-field
                  :key="`specialPatient_${field.name}`"
                  :ref="field.name"
                  :id="field.name"
                  :validators.sync="$v.formData[field.name]"
                  :form.sync="formData"
                  :field="field"
                  :class="[
                    field.value && 'has-value',
                    (field.name === 'date' && $v.formData[field.name].$invalid) && 'has-error'
                  ]"
                  class="modal-form__input"
                  prefix="specialPatientForm_"
                />
              </div>
            </div>
          </div>

          <!-- Plano de Saúde -->
          <transition-group name="fade">
            <div
              v-for="healthPlan in healthInsurancePlans"
              :key="healthPlan.id"
              class="special-patient__health-plan"
              @mouseenter="healthPlan.showRemove = true"
              @mouseleave="healthPlan.showRemove = false"
            >
              <dr-special-patient-health-insurance
                v-model.lazy="healthPlan.obj"
                :id="healthPlan.id"
                @change="validateHealthPlan(healthPlan)"
              />

              <transition name="fade">
                <div
                  v-if="healthPlan.showRemove"
                  class="special-patient__remove"
                  @click.prevent="removeHealthPlan(healthPlan.id)"
                >
                  <i class="icon icon-cross" />
                </div>
              </transition>
            </div>
          </transition-group>

          <div class="modal-form__wrapper">
            <div class="modal-form__item">
              <div class="modal-form__col modal-form__col--icon"></div>

              <div class="modal-form__col">
                <div class="modal-form__field">
                  <button
                    class="modal-form__btn"
                    @click.prevent="addHealthPlan()"
                  >
                    Adicionar plano de saúde
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <footer class="modal-form__footer">
        <dr-submit
          :loading="loading"
          class="appointment-simple__submit"
          text="Salvar"
        />

        <button
          v-if="patientId"
          class="modal-form__remove"
          @click.prevent="deletePatient()"
        >
          Remover
        </button>
      </footer>
    </form>
  </div>
</template>

<script>
import DrSpecialPatientHealthInsurance from '@/components/modals/specialPatientHealthInsurance'
import { formData, validations } from '@/data/formData/specialPatient'
import DrSubmit from '@/components/form/submit'
import DrField from '@/components/form/field'
import { mapGetters } from 'vuex'

export default {
  name: 'DrFormSpecialPatient',

  props: {
    patientId: {
      required: false,
      type: Number,
      default: () => null
    }
  },

  components: {
    DrSpecialPatientHealthInsurance,
    DrField,
    DrSubmit
  },

  validations: {
    formData: validations
  },

  data () {
    return {
      formData,
      patientOfficeId: null,
      healthInsurancePlans: [],
      counter: 0,
      active: true,
      loading: false,
      shouldSearchAddress: false
    }
  },

  computed: {
    ...mapGetters({
      doctorOffices: 'getOffices',
      office: 'getCurrentOffice'
    }),

    prefix () {
      return this.fn_IS_DOCTOR()
        ? '/doctor'
        : `/secretary/doctor/${this.$route.params.doctorId}`
    }
  },

  watch: {
    'formData.addressCep.value': {
      deep: false,
      immediate: false,
      handler (newCep, oldCep) {
        if (this.shouldSearchAddress) {
          if (
            newCep !== oldCep &&
            newCep &&
            /^\d{5}-\d{3}$/.test(newCep)
          ) {
            this.searchAddress(newCep)
          } else {
            this.formData.addressStreet.value = ''
            this.formData.addressNumber.value = ''
            this.formData.addressComplement.value = ''
            this.formData.addressDistrict.value = ''
            this.formData.addressCityName.value = ''
            this.formData.addressUfName.value = ''
            this.formData.addressLatitude.value = ''
            this.formData.addressLongitude.value = ''
          }
        }
      }
    },

    'formData.specialPatientDdiContactPhone.value': {
      deep: false,
      immediate: false,
      handler (next, before) {
        if (next !== before) {
          const { mask } = this.formData.specialPatientDdiContactPhone.options
            .find((option) => option.id === next)

          this.formData.specialPatientContactPhone.mask = [mask]

          if (next === 'br') {
            this.formData.specialPatientContactPhone.mask = ['(##) ####-####', '(##) #####-####']
          }
        }
      }
    }
  },

  created () {
    this.patientOfficeId = this.office.id
    this.formData.specialPatientDdiContactPhone.value = 'br'

    if (this.patientId) {
      this.getPatient()
    } else {
      this.shouldSearchAddress = true
    }
  },

  destroyed () {
    this.destroyObject()
  },

  methods: {
    close () {
      this.destroyObject()
      this.active = false
      this.$emit('cancel')
    },

    searchAddress (cep) {
      this.$http
        .get(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`)
        .then((res) => {
          if (res.data) {
            const { data } = res
            const setDisable = (object, property, value) => {
              property.forEach(p => (object[p].disabled = value))
            }

            setDisable(
              this.formData,
              [
                'addressStreet',
                'addressDistrict',
                'addressCityName',
                'addressUfName'
              ],
              true
            )
            // se encontrou o CEP, mas sem rua e bairro,
            // habilita estes campos para edição
            if (
              !data.logradouro ||
              !data.bairro ||
              (data.logradouro.length + data.bairro.length) === 0
            ) {
              setDisable(
                this.formData,
                ['addressDistrict', 'addressStreet'],
                false
              )
            }

            this.formData['addressStreet'].value = data.logradouro
            this.formData['addressDistrict'].value = data.bairro
            this.formData['addressCityName'].value = data.localidade
            this.formData['addressUfName'].value = data.uf

            if ('erro' in data) {
              this.$store.dispatch('setModalConfirm', {
                title: 'CEP não encontrado',
                body: 'Deseja pesquisar um outro CEP ou editar o endereço manualmente?',
                confirmText: 'Editar',
                cancelText: 'Pesquisar',
                beforeClose: (v) => {
                  if (v) {
                    setDisable(
                      this.formData,
                      ['addressStreet', 'addressDistrict', 'addressCityName', 'addressUfName'],
                      false
                    )
                  }
                  this.$store.dispatch('setModalConfirm')
                },
                size: 'md',
                active: true
              })
            } else {
              const key = this.$store.getters.$_getMapsApi
              const address = `${this.formData['addressStreet'].value},
                ${this.formData['addressNumber'].value},
                ${this.formData['addressDistrict'].value},
                ${this.formData['addressCityName'].value} -
                ${this.formData['addressUfName'].value}`

              this.$http
                .get(`https://maps.google.com/maps/api/geocode/json?key=${key}&address=${address}`)
                .then((res) => {
                  if (res.status === 200) {
                    const { data } = res
                    if (data.results.length > 0) {
                      const { geometry } = data.results[0]
                      this.formData['addressLatitude'].value = geometry.location.lat
                      this.formData['addressLongitude'].value = geometry.location.lng
                    }
                  }
                })
                .catch(err => console.log('err', err))
            }
          }
        })
        .catch(err => console.log('err', err))
    },

    addHealthPlan (obj = {}) {
      const healthPlan = Object.keys(obj).length === 0
        ? { healthPlan: null, walletNumber: null, walletValidity: null }
        : obj

      this.healthInsurancePlans.push({
        id: `healthPlan_${this.counter++}`,
        showRemove: false,
        obj: healthPlan,
        isValid: true
      })
    },

    removeHealthPlan (id = null) {
      if (id) {
        const index = this.healthInsurancePlans.findIndex(h => h.id === id)
        this.healthInsurancePlans.splice(index, 1)
      }
    },

    validateHealthPlan (healthPlan) {
      const { obj } = healthPlan

      if (obj.walletNumber === '' || obj.walletNumber === null) {
        healthPlan.isValid = false
        return
      }

      if (
        obj.walletValidity === '' ||
        obj.walletValidity === null ||
        this.$moment(obj.walletValidity, 'DD/MM/YYYY', true).isBefore(this.$moment())
      ) {
        healthPlan.isValid = false
        return
      }

      if (
        obj.healthPlan === '' ||
        obj.healthPlan === null ||
        obj.healthPlan.id === '' ||
        obj.healthPlan.id === null ||
        obj.healthPlan.name === '' ||
        obj.healthPlan.id === null
      ) {
        healthPlan.isValid = false
        return
      }

      healthPlan.isValid = true
    },

    destroyObject () {
      Object.keys(this.formData).forEach((key) => {
        this.formData[key].value = null
      })

      this.healthInsurancePlans = []
    },

    isEmpty (obj) {
      if (
        obj.healthPlan === null &&
        obj.walletValidity === null &&
        obj.walletNumber === null
      ) {
        return true
      }

      return false
    },

    buildApiObject () {
      const name = this.formData.specialPatientName.value
      const gender = this.formData.specialPatientGender.value
      const contactPhone = this.formData.specialPatientContactPhone.value
        ? this.fn_NUMBERS_ONLY(this.formData.specialPatientContactPhone.value)
        : null
      const cpf = this.formData.specialPatientCpf.value
        ? this.fn_NUMBERS_ONLY(this.formData.specialPatientCpf.value)
        : null
      const dateOfBirth = this.formData.specialPatientDateOfBirth.value
        ? this.$moment(
          this.formData.specialPatientDateOfBirth.value,
          'DD/MM/YYYY',
          true).format('YYYY-MM-DD')
        : null
      const email = this.formData.specialPatientEmail.value
      const nationalHealthCard = this.formData.specialPatientNationalHealthCard.value
      const rg = this.formData.specialPatientRg.value
      const ddiContactPhone = this.formData.specialPatientContactPhone.value
        ? this.formData.specialPatientDdiContactPhone.options
          .find(
            (option) => option.id === this.formData.specialPatientDdiContactPhone.value
          )
          .code
        : ''

      const data = {
        name,
        gender,
        ddiContactPhone,
        contactPhone,
        cpf,
        dateOfBirth,
        email,
        nationalHealthCard,
        rg
      }

      const cep = this.formData.addressCep.value
      if (cep) {
        data.address = {
          cep: cep ? this.fn_NUMBERS_ONLY(cep) : '',
          city: { name: this.formData.addressCityName.value },
          uf: this.formData.addressUfName.value,
          street: this.formData.addressStreet.value,
          number: this.formData.addressNumber.value,
          complement: this.formData.addressComplement.value,
          district: this.formData.addressDistrict.value,
          latitude: this.formData.addressLatitude.value,
          longitude: this.formData.addressLongitude.value
        }
      }

      if (this.healthInsurancePlans.length > 0) {
        const healthPlans = []

        this.healthInsurancePlans.forEach((plan) => {
          if (plan.isValid && !this.isEmpty(plan.obj)) {
            healthPlans.push({
              healthInsurance: plan.obj.healthPlan.id,
              walletNumber: plan.obj.walletNumber,
              walletValidity: this.$moment(plan.obj.walletValidity, 'DD/MM/YYYY', true).format('YYYY-MM-DD')
            })
          }
        })

        data.specialPatientHealthInsurances = healthPlans
      }

      return data
    },

    getPatient () {
      if (this.patientId) {
        const url = `${this.prefix}/special_patient/${this.patientId}`
        this.$http
          .get(url, this.$store.getters.getAuthHeader)
          .then((res) => {
            const { patient } = res.data
            this.formData.specialPatientName.value = patient['name']
            this.formData.specialPatientEmail.value = patient['email']
            this.formData.specialPatientContactPhone.value = patient['contact_phone']
            this.formData.specialPatientGender.value = patient['gender']
            this.formData.specialPatientDateOfBirth.value = patient['date_of_birth']
              ? this.$moment(patient['date_of_birth']).format('DD/MM/YYYY')
              : ''
            this.formData.specialPatientCpf.value = patient['cpf']
            this.formData.specialPatientRg.value = patient['rg']
            this.formData.specialPatientNationalHealthCard.value = patient['national_health_card']

            if (patient['ddi_contact_phone']) {
              this.formData.specialPatientDdiContactPhone.value = this.formData.specialPatientDdiContactPhone.options
                .find((option) => option.code === patient['ddi_contact_phone'])
                .id
            }

            if (patient.address) {
              this.formData.addressCep.value = patient.address['cep']
              this.formData.addressStreet.value = patient.address['street']
              this.formData.addressNumber.value = patient.address['number']
              this.formData.addressComplement.value = patient.address['complement']
              this.formData.addressDistrict.value = patient.address['district']
              this.formData.addressCityName.value = patient.address['city']['name']
              this.formData.addressUfName.value = patient.address['uf']['name']
              this.formData.addressLatitude.value = patient.address['latitude']
              this.formData.addressLongitude.value = patient.address['longitude']
            }

            if (patient['special_patient_health_insurances'].length > 0) {
              patient['special_patient_health_insurances'].forEach((plan) => {
                const healthPlan = plan['health_insurance']
                const walletNumber = plan['wallet_number']
                const walletValidity = this.$moment(plan['wallet_validity']).format('DD/MM/YYYY')

                this.addHealthPlan({
                  healthPlan,
                  walletNumber,
                  walletValidity
                })
              })
            }
          })
          .catch(err => this.fn_HANDLE_ERROR(err))
          .finally(() => (this.shouldSearchAddress = true))
      }
    },

    deletePatient () {
      this.$store.dispatch('setModalConfirm', {
        title: 'Confirmação',
        body: 'Tem certeza que deseja apagar esse paciente? Esta ação não poderá ser desfeita.',
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        beforeClose: (v) => {
          if (v) {
            const url = `${this.prefix}/doctor_office/${this.patientOfficeId}/special_patient/${this.patientId}`
            this.fn_SHOW_PROGRESS()
            this.$http.delete(url, this.$store.getters.getAuthHeader)
              .then((res) => {
                this.$store.dispatch('setModalAlert', {
                  title: 'Sucesso',
                  body: `Paciente removido com sucesso.`,
                  size: 'md',
                  active: true
                })

                this.close()
                this.$emit('success')
              })
              .catch(err => this.fn_HANDLE_ERROR(err))
              .finally(this.fn_HIDE_PROGRESS)
          }
          this.$store.dispatch('setModalConfirm')
        },
        size: 'md',
        active: true
      })
    },

    save () {
      if (
        this.$v.$invalid ||
        this.healthInsurancePlans
          .filter(plan => !this.isEmpty(plan.obj))
          .some(plan => !plan.isValid)
      ) {
        this.$v.$touch()
        return
      }

      const suffix = this.patientId
        ? `/${this.patientId}`
        : ''
      const url = `${this.prefix}/doctor_office/${this.patientOfficeId}/special_patient${suffix}`
      const headers = this.$store.getters.getAuthHeader.headers
      const method = this.patientId ? 'put' : 'post'
      const data = { 'special_patient': this.buildApiObject() }

      this.loading = true
      this.fn_SHOW_PROGRESS()
      this.$http({ url, method, data, headers })
        .then((res) => {
          const msg = (this.patientId) ? 'editado' : 'cadastrado'

          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: `Paciente ${msg} com sucesso.`,
            size: 'md',
            active: true
          })

          this.close()
          this.$emit('success')
        })
        .catch(err => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.loading = false
          this.fn_HIDE_PROGRESS()
        })
    }
  }
}
</script>
